import "./form-registration.scss";

import { RegistrationFormSchema } from "@factory/form/form.rule";
import { useQyGetUtilService } from "@factory/query/util-service.query";
import { sexOptions } from "@shared/data/sex";
import { LabelValue } from "@shared/models/label-value.interface";
import CheckboxControl from "@shared/ui/hook-form/checkbox-control/checkbox-control";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import InputControl from "@shared/ui/hook-form/input-control/input-control";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import InputTextareaControl from "@shared/ui/hook-form/input-textarea-control/input-textarea-control";
import TextBlock from "@shared/ui/text-block/text-block";
import { calculateAge } from "@shared/utility/calculage-age";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { useFilterRegistrationContext } from "../filter-registration/filter-registration.context";

interface FormRegistrationProps {
  serviceOptions?: LabelValue[];
  isFetchingServices?: boolean;
  isEdit?: boolean;
}

type InputFormSchema = RegistrationFormSchema;
export function FormRegistration({
  serviceOptions,
  isFetchingServices = false,
  isEdit,
}: FormRegistrationProps) {
  const { control, watch, setValue } = useFormContext<RegistrationFormSchema>();
  const birthday = watch("birthday");
  const isTakingMedication = watch("isTakingMedication");
  const hasAllergies = watch("hasAllergies");
  const hasMedicalConditions = watch("hasMedicalConditions");
  const wasRecentlyUnwell = watch("wasRecentlyUnwell");
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null);
  const { getFilterEntityValues } = useFilterRegistrationContext();
  const { service } = getFilterEntityValues();
  const { client } = useParams();

  const { data: utilServiceResponse } = useQyGetUtilService(
    "",
    999,
    0,
    undefined,
    undefined,
    isEdit
  );
  const utilServiceData = utilServiceResponse?.data || [];
  const mappedUtilService = utilServiceData.map(
    (item) =>
      ({
        label: item.name,
        value: item.code,
      } as LabelValue)
  );

  const publicService = useCallback(() => {
    if (service) {
      setValue("service", service);
      return null;
    }

    const optionsToUse = isEdit ? mappedUtilService : serviceOptions || [];

    return (
      <DropdownControl<RegistrationFormSchema>
        control={control}
        name="service"
        label="Service Name"
        className="w-full"
        options={optionsToUse}
        placeholder={
          isFetchingServices ? "Loading services..." : "Select a service"
        }
      />
    );
  }, [service, isEdit, serviceOptions, isFetchingServices, setValue, control]);

  useEffect(() => {
    if (birthday) {
      const age = calculateAge(birthday);
      setCalculatedAge(age);
    }
  }, [birthday]);

  return (
    <div className="form-registration-container">
      <h4 className="text-lg font-semibold mb-2">Personal Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <InputControl<InputFormSchema>
            control={control}
            name="firstName"
            label="First Name"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter firstName"
          />
          <InputControl<InputFormSchema>
            control={control}
            name="middleName"
            label="Middle Name"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter middleName"
          />
          <InputControl<InputFormSchema>
            control={control}
            name="lastName"
            label="Last Name"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter lastName"
          />
        </div>

        <div>
          <InputDateControl<InputFormSchema>
            control={control}
            name="birthday"
            label="Birthday"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter birthday"
          />
          <TextBlock
            label="Age"
            value={
              calculatedAge !== null ? calculatedAge.toString() : undefined
            }
            mode="horizontal"
            className="w-full pb-2 mt-10 mb-6"
          />
          <DropdownControl<InputFormSchema>
            control={control}
            name="gender"
            label="Sex"
            className="w-full "
            containerClassName="pb-2"
            placeholder="Enter sex"
            options={sexOptions}
          />
        </div>
      </div>
      <h4 className="text-lg font-semibold">Contract & Address</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <InputControl<InputFormSchema>
            control={control}
            name="mobileNumber"
            label="Contact Number"
            className="w-full mt-0"
            containerClassName="pb-2"
            placeholder="Enter contact number"
          />
          <InputControl<InputFormSchema>
            control={control}
            name="email"
            label="Email Address"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter email address"
          />
        </div>
        <div>
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="address"
            label="Address"
            className="w-full textarea-responsive"
            containerClassName="pb-2"
            placeholder="Enter address"
          />
        </div>
      </div>

      <h4 className="text-lg font-semibold">Medical Information</h4>
      <section className="flex flex-col md:flex-col ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <CheckboxControl<InputFormSchema>
              control={control}
              name="isTakingMedication"
              label="Is Taking Medication"
              containerClassName="pb-2"
            />
            {isTakingMedication ? (
              <InputTextareaControl<InputFormSchema>
                control={control}
                name="medicationDetails"
                label="Medication Details"
                className="w-full textarea-small"
                containerClassName="pb-2"
                placeholder="Enter medication Details"
              />
            ) : null}
            <CheckboxControl<InputFormSchema>
              control={control}
              name="hasAllergies"
              label="Has Allergies"
              containerClassName="pb-2"
            />
            {hasAllergies ? (
              <InputTextareaControl<InputFormSchema>
                control={control}
                name="allergyDetails"
                label="Allergy Details"
                className="w-full textarea-small"
                containerClassName="pb-2"
                placeholder="Enter Allergy Details"
              />
            ) : null}
          </div>
          <div>
            <CheckboxControl<InputFormSchema>
              control={control}
              name="hasMedicalConditions"
              label="Has Medical Conditions"
              containerClassName="pb-2"
            />
            {hasMedicalConditions ? (
              <InputTextareaControl<InputFormSchema>
                control={control}
                name="medicalConditonDetails"
                label="Medical Condition Details"
                className="w-full textarea-small"
                containerClassName="pb-2"
                placeholder="Enter Medical Condition Details"
              />
            ) : null}
            <CheckboxControl<InputFormSchema>
              control={control}
              name="wasRecentlyUnwell"
              label="Was Recently Unwell"
              containerClassName="pb-2"
            />
            {wasRecentlyUnwell ? (
              <InputTextareaControl<InputFormSchema>
                control={control}
                name="unwellDetails"
                label="Unwell Details"
                className="w-full textarea-small"
                containerClassName="pb-2"
                placeholder="Enter Unwell Details"
              />
            ) : null}
          </div>
        </div>
        <div>
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="otherMedicalConditions"
            label="Other Medical Conditions"
            className="w-full textarea-responsive"
            containerClassName="pb-2"
            placeholder="Enter Other Medical Conditions"
          />
        </div>
      </section>
      <h4 className="text-lg font-semibold">Appointment Details</h4>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {publicService()}
          <InputDateControl<InputFormSchema>
            control={control}
            name="date"
            label="Date"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter date"
          />
          <InputDateControl<InputFormSchema>
            type="time"
            control={control}
            name="time"
            label="Time"
            className="w-full"
            containerClassName="pb-2"
            placeholder="Enter time"
          />
        </div>
        <div>
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="otherRemarks"
            label="Other Remarks"
            className="w-full textarea-large"
            containerClassName="pb-2"
            placeholder="Enter Other Remarks"
          />
        </div>
      </section>
      <div className="mt-4">
        <CheckboxControl<InputFormSchema>
          control={control}
          name="consentAgreement"
          label={`This is to certify that you are willing to submit sensitive information to ${
            client ?? "the clinic"
          } to proceed with booking reservation.`}
          containerClassName="pb-2"
        />
      </div>
    </div>
  );
}

export default FormRegistration;
